// Presentación de usuarios: una sola regla de nombre, estado y empresas para la
// pantalla de Usuarios y los selectores que listan personas (UX-COPY-01).
import { apiErrorReason } from '@/lib/api';

import type { AdminUser, UserCompany } from './types';

type BadgeCls = 'b-ok' | 'b-warn' | 'b-mut';

export function userDisplayName(
  u: Pick<AdminUser, 'displayName' | 'firstName' | 'lastName' | 'email'>,
): string {
  return u.displayName?.trim() || `${u.firstName} ${u.lastName}`.trim() || u.email;
}

const STATUS: Record<string, { label: string; cls: BadgeCls }> = {
  active: { label: 'Activo', cls: 'b-ok' },
  pending: { label: 'Pendiente', cls: 'b-warn' },
  inactive: { label: 'Desactivado', cls: 'b-mut' },
};

export function userStatusBadge(status: string): { label: string; cls: BadgeCls } {
  return STATUS[status] ?? { label: 'Otro estado', cls: 'b-mut' };
}

function empresas(n: number): string {
  return `${n} ${n === 1 ? 'empresa' : 'empresas'}`;
}

/** Empresas visibles por nombre; las demás solo se cuentan (no se revelan). */
export function companiesSummary(
  companies: UserCompany[],
  otherCount: number,
): { names: string[]; more: string | null; empty: string | null } {
  const names = companies.map((c) => c.name);
  if (names.length === 0 && otherCount === 0) {
    return { names, more: null, empty: 'Sin empresa asignada' };
  }
  if (otherCount === 0) {
    return { names, more: null, empty: null };
  }
  const more = names.length === 0 ? empresas(otherCount) : `y ${empresas(otherCount)} más`;
  return { names, more, empty: null };
}

const ADMIN_REJECTIONS: Record<string, string> = {
  last_user_manager:
    'Es el único usuario que puede administrar usuarios. Asigna ese rol a otra persona antes.',
  multi_company_target:
    'Pertenece también a otras empresas: lo gestiona un administrador del grupo.',
  target_outranks: 'Administra todo el grupo: solo otro administrador del grupo puede gestionarlo.',
  self_deactivation: 'No puedes desactivar tu propio usuario.',
};

/** Mensaje de un rechazo del backend en la administración de usuarios: qué pasó y qué
 *  hacer. Si el motivo no es conocido (p. ej. la red), el reintento del llamador. */
export function adminActionError(err: unknown, retryMessage: string): string {
  const reason = apiErrorReason(err);
  return (reason && ADMIN_REJECTIONS[reason]) || retryMessage;
}
